import { ImageOff } from "lucide-react";
import { FileType } from "../../types/dtos/fileAssets/FileAssetType";
import type FileAssetDTO from "../../types/dtos/fileAssets/FileAssetDTO";
import type { FileSize } from "../../types/ui/files/FileProps";
import { useBlobUrl } from "../../hooks/helpers/storageAccount";
import { Image } from "../ui/image/Image";

interface FilePreviewProps extends Omit<FileAssetDTO, "Id" | "CreatedOn"> {
  FileSize: FileSize;
  className?: string;
}

const ImageBlobPreview = ({
  FileSize,
  OriginalFileName,
  FilePath,
  ContentType,
  FileSizes,
  className,
}: FilePreviewProps) => {
  let extension = OriginalFileName.split(".").pop() || "";

  if (
    ContentType === FileType.Pdf &&
    (FileSize === "small" || FileSize === "medium")
  ) {
    extension = "jpg";
  }

  const getSizePriority = (requested: FileSize): FileSize[] => {
    switch (requested) {
      case "small":
        return ["small"];

      case "medium":
        return ["medium", "original"];

      case "large":
        return ["large", "medium", "original"];

      default:
        return ["original"];
    }
  };

  const availableSizes: FileSize[] = FileSizes
    ? (FileSizes.split(",").map((x) => x.trim()) as FileSize[])
    : ["original"];

  const sizePriority = getSizePriority(FileSize);

  const resolvedSize =
    sizePriority.find((size) => availableSizes.includes(size)) ?? "original";

  const filePathWithExtension = `${FilePath}\\${resolvedSize}.${extension}`;

  const blobUrl = useBlobUrl(filePathWithExtension);

  return (
    <Image
      src={blobUrl}
      alt={OriginalFileName}
      className={`object-contain w-full h-full ${className}`}
      fallback={
        <div className="relative w-full h-full bg-gray-100">
          <ImageOff
            className="absolute inset-0 m-auto w-20 h-20 text-gray-500 opacity-20"
            strokeWidth={1}
          />
        </div>
      }
    />
  );
};

export default function FilePreview({
  FileSize,
  OriginalFileName,
  FileName,
  FilePath,
  FileSizes,
  ContentType,
  className,
}: FilePreviewProps) {
  const extension = OriginalFileName.split(".").pop() || "";

  const filePathWithExtension = `${FilePath}\\original.${extension}`;
  const originalBlobUrl = useBlobUrl(filePathWithExtension);

  const isImage =
    ContentType === FileType.ImageJpeg ||
    ContentType === FileType.ImagePng ||
    ContentType.startsWith("image/") ||
    ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(
      extension.toLowerCase(),
    );

  const isPdf =
    ContentType === FileType.Pdf ||
    OriginalFileName.toLowerCase().endsWith(".pdf");

  const isOfficeFile =
    ContentType === FileType.MsWord ||
    ContentType === FileType.ExcelMacroenabled12 ||
    OriginalFileName.toLowerCase().endsWith(".doc") ||
    OriginalFileName.toLowerCase().endsWith(".docx") ||
    OriginalFileName.toLowerCase().endsWith(".xls") ||
    OriginalFileName.toLowerCase().endsWith(".xlsx");

  if (isImage || (isPdf && (FileSize == "small" || FileSize == "medium"))) {
    return (
      <ImageBlobPreview
        FileSize={FileSize}
        OriginalFileName={OriginalFileName}
        FileName={FileName}
        FilePath={FilePath}
        FileSizes={FileSizes}
        ContentType={ContentType}
        className={className}
      />
    );
  }

  if (isOfficeFile) {
    if (FileSize === "small" || FileSize === "medium") {
      return (
        <>
          <div
            className={`w-full h-full flex items-center justify-center rounded-t-md  shadow-inner ${className}`}
            style={{ backgroundColor: "#2368c4" }}
          >
            <Image
              src="/images/file-icons/word.png"
              alt="Word File"
              className="w-14 h-14 object-contain"
            />
          </div>
        </>
      );
    } else {
      const previewUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
        originalBlobUrl,
      )}&embedded=true`;

      return (
        <>
          <iframe
            src={previewUrl}
            className={`w-dvh h-[88dvh] ${className}`}
            title={OriginalFileName}
          />
          <div className="absolute inset-0 bg-transparent" />
        </>
      );
    }
  }

  if (isPdf) {
    const previewUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
      originalBlobUrl,
    )}&embedded=true`;

    return (
      <div className="relative w-full h-full bg-gray-200">
        <iframe
          src={previewUrl}
          className={`w-dvh h-[88dvh] ${className}`}
          title={OriginalFileName}
        />
      </div>
    );
  }
}
